'use client';

import { useId } from 'react';
import { Icon } from '@galapagos/design-system/web';
import { sizing } from '@galapagos/design-system/sizing';
import { splitInlineLinkTemplate } from '@galapagos/shared';
import { useTranslation } from 'react-i18next';
import type { LegacyIncentive } from '@/services/legacy-incentive';
import { ensureIncentiveReminderStyle } from '../../wrap-up/incentive-reminder-style';

const INCENTIVE_COPY = {
  'amazon-gift-card': {
    title: 'wrapUp.booking.incentive.amazonGiftCard.title',
    description: 'wrapUp.booking.incentive.amazonGiftCard.description',
    image: '/signup/incentive-amazon-gift-card.svg',
  },
} as const satisfies Record<LegacyIncentive, { title: string; description: string; image: string }>;

export interface IncentiveReminderProps {
  incentive: LegacyIncentive;
  onClose: () => void;
}

/**
 * Production's reminder for a link-driven acquisition incentive, shown on the
 * first-call booking step. Unlike production's card, it stays readable by
 * assistive technology and its close control is a real button.
 */
export function IncentiveReminder({ incentive, onClose }: IncentiveReminderProps) {
  const { t } = useTranslation('signup');
  const titleId = useId();
  ensureIncentiveReminderStyle();
  const copy = INCENTIVE_COPY[incentive];
  return (
    <section
      className="signup-incentive"
      aria-labelledby={titleId}
      data-testid="signup-booking-incentive"
    >
      <div className="signup-incentive__text">
        <p id={titleId} className="signup-incentive__title">
          {t(copy.title)}
        </p>
        <p className="signup-incentive__description">
          {splitInlineLinkTemplate(t(copy.description)).map((run, index) =>
            run.kind === 'link' && run.tag === 'strong' ? (
              <strong key={index}>{run.text}</strong>
            ) : (
              run.text
            ),
          )}
        </p>
      </div>
      <img className="signup-incentive__image" src={copy.image} alt="" />
      <button
        type="button"
        className="signup-incentive__close"
        aria-label={t('wrapUp.booking.incentive.close')}
        data-analytics-action="signup.booking-incentive-close.activate"
        data-testid="signup-booking-incentive-close"
        onClick={onClose}
      >
        <Icon name="close" size={sizing.icon.sm} aria-hidden />
      </button>
    </section>
  );
}
